import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Star, MessageSquareQuote, Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";

export default function Reviews({ product, onRatingUpdated }) {
  const { user, login } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/products/${product.slug}/reviews`); setReviews(r.data); }
    catch { setReviews([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [product.slug]);

  const alreadyReviewed = user && reviews.some(r => r.user_id === user.user_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { login(); return; }
    if (rating < 1) return toast.error("Please pick a star rating");
    if (!body.trim()) return toast.error("Please write your review");
    setSubmitting(true);
    try {
      await api.post(`/products/${product.product_id}/reviews`, { rating, title, body });
      toast.success("Review posted — thanks for sharing!");
      setRating(0); setTitle(""); setBody("");
      await load();
      onRatingUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post review");
    } finally { setSubmitting(false); }
  };

  const buckets = [5,4,3,2,1].map(s => ({
    stars: s, count: reviews.filter(r => r.rating === s).length
  }));
  const totalRealReviews = reviews.length;

  return (
    <section className="mt-16 max-w-7xl mx-auto px-4 md:px-8">
      <div className="grid md:grid-cols-3 gap-8">
        {/* Left — summary */}
        <div className="md:col-span-1">
          <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">What people think</div>
          <h2 className="font-serif-display text-4xl md:text-5xl">Reviews</h2>
          <div className="mt-5 bg-white border border-stone-200 rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <div className="font-serif-display text-5xl text-[#3B2F2F]">{product.rating}</div>
              <div>
                <div className="flex text-[#D4AF37]">
                  {Array.from({length:5}).map((_,i)=><Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-current":""}`}/>)}
                </div>
                <div className="text-xs text-stone-500 mt-1">{product.review_count || 0} reviews</div>
              </div>
            </div>
            {totalRealReviews > 0 && (
              <div className="mt-5 space-y-2">
                {buckets.map(b => {
                  const pct = totalRealReviews > 0 ? (b.count/totalRealReviews)*100 : 0;
                  return (
                    <div key={b.stars} className="flex items-center gap-2 text-xs">
                      <div className="flex items-center gap-1 w-8"><span>{b.stars}</span><Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]"/></div>
                      <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }}/>
                      </div>
                      <div className="text-stone-500 w-6 text-right">{b.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right — form + list */}
        <div className="md:col-span-2 space-y-6">
          {/* Form */}
          {!user ? (
            <div className="bg-gradient-to-br from-[#FEF3E2] to-[#F5DEC7]/40 border border-[#D4AF37]/40 rounded-3xl p-6">
              <div className="flex items-start gap-3">
                <MessageSquareQuote className="w-6 h-6 text-[#D4AF37] shrink-0 mt-0.5"/>
                <div className="flex-1">
                  <div className="font-outfit font-semibold text-[#3B2F2F]">Share your thoughts on this coffee</div>
                  <div className="text-sm text-stone-600 mt-1">Sign in to leave a review and help other coffee lovers choose.</div>
                  <button onClick={login} data-testid="review-login-btn" className="mt-3 bg-[#3B2F2F] text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-black">
                    Sign in to write a review
                  </button>
                </div>
              </div>
            </div>
          ) : alreadyReviewed ? (
            <div className="bg-[#E8EDDF] border border-[#2E4A35]/30 rounded-3xl p-6 flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-[#2E4A35] shrink-0 mt-0.5"/>
              <div>
                <div className="font-outfit font-semibold text-[#2E4A35]">Review submitted</div>
                <div className="text-sm text-stone-600 mt-1">Thanks for sharing your thoughts. You can only review a product once.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-6 md:p-7 space-y-4" data-testid="review-form">
              <div>
                <div className="font-outfit font-semibold text-lg">Write a review</div>
                <div className="text-xs text-stone-500">Rate your experience with {product.name}</div>
              </div>

              {/* Star picker */}
              <div className="flex items-center gap-2">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button"
                          onMouseEnter={()=>setHoverRating(s)} onMouseLeave={()=>setHoverRating(0)}
                          onClick={()=>setRating(s)} data-testid={`review-star-${s}`}
                          className="p-1 focus:outline-none transition-transform hover:scale-110">
                    <Star className={`w-8 h-8 transition-colors ${
                      (hoverRating || rating) >= s ? "fill-[#D4AF37] text-[#D4AF37]" : "text-stone-300"
                    }`}/>
                  </button>
                ))}
                {rating > 0 && <span className="text-sm text-stone-600 ml-2">{["","Meh","Okay","Good","Great","Love it!"][rating]}</span>}
              </div>

              <Input value={title} onChange={e=>setTitle(e.target.value)}
                     placeholder="Give it a headline (optional)" maxLength={80}
                     data-testid="review-title"/>

              <Textarea value={body} onChange={e=>setBody(e.target.value)}
                        placeholder="What did you like? How does it taste? Would you recommend it?"
                        required maxLength={2000} rows={4}
                        data-testid="review-body"/>

              <button type="submit" disabled={submitting} data-testid="review-submit"
                      className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-5 py-2.5 font-medium hover:bg-black disabled:bg-stone-400">
                <Send className="w-4 h-4"/> {submitting ? "Posting…" : "Post review"}
              </button>
            </form>
          )}

          {/* Reviews list */}
          <div className="space-y-4">
            {loading && <div className="text-stone-500 text-sm">Loading reviews…</div>}
            {!loading && reviews.length === 0 && (
              <div className="text-stone-500 text-sm bg-white/60 border border-dashed border-stone-200 rounded-2xl p-6 text-center">
                Be the first to review {product.name}.
              </div>
            )}
            {reviews.map(r => (
              <div key={r.review_id} className="bg-white border border-stone-100 rounded-2xl p-5 hover:border-[#D4AF37]/30 transition-colors" data-testid={`review-${r.review_id}`}>
                <div className="flex items-start gap-3">
                  {r.user_picture ? (
                    <img src={r.user_picture} alt="" className="w-10 h-10 rounded-full object-cover"/>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F5F0E6] flex items-center justify-center font-serif-display text-lg text-[#3B2F2F]">
                      {(r.user_name || "?")[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-outfit font-semibold text-sm">{r.user_name || "Anonymous"}</span>
                      <span className="text-xs text-stone-400">·</span>
                      <span className="text-xs text-stone-500">{new Date(r.created_at).toLocaleDateString(undefined,{day:"numeric",month:"short",year:"numeric"})}</span>
                    </div>
                    <div className="flex text-[#D4AF37] mt-1">
                      {Array.from({length:5}).map((_,i)=><Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-current" : ""}`}/>)}
                    </div>
                    {r.title && <div className="font-serif-display text-xl mt-2 text-[#1C1917]">{r.title}</div>}
                    <p className="text-sm text-stone-700 mt-2 leading-relaxed whitespace-pre-line">{r.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
